import type { InternalRequest } from '../client.js';
import type { BroadcastChannel, BroadcastEstimate, BroadcastCapDecision } from './broadcasts.js';

// Mirrors server/src/muster/muster.service.ts's RosterSpec — who a muster
// targets. The client sends only this SPEC, never a resolved recipient
// list; server-side re-resolution is the unbypassable security boundary,
// same as Broadcast's own AudienceSpec.
export type MusterRosterSpec =
  | { kind: 'group'; groupIds: number[] }
  | { kind: 'explicit'; userIds: string[] }
  | { kind: 'all' };

// Mirrors ZoneSpec — null means no zone (a coordinate-free roll-call, spec §5).
export type MusterZoneSpec =
  | { kind: 'geofence'; geofenceId: number }
  | { kind: 'radius'; latitude: number; longitude: number; radiusM: number }
  | null;

// Mirrors server/src/muster/muster-state.ts.
export type MusterState = 'awaiting' | 'safe' | 'needs_help' | 'unaccounted' | 'excluded';
export type MusterReason =
  | 'self_report'
  | 'marked_by_coordinator'
  | 'declared_missing'
  | 'no_response'
  | 'excluded';
export type MusterSource = 'self' | 'coordinator' | 'close';

/** The {lat, lng, fixAt, inZone} shape stamped into muster_people at declare
 *  time and at close time — RosterPerson.snapshot, the closed-board branch,
 *  and ExportPerson's two snapshot columns all share it verbatim. */
export interface MusterSnapshot {
  lat: number;
  lng: number;
  fixAt: string;
  inZone: boolean | null;
}

export interface MusterCounts {
  awaiting: number;
  safe: number;
  needs_help: number;
  unaccounted: number;
  excluded: number;
}

/** Mirrors server/src/muster/muster-closeout.ts's MusterSummary — written by
 *  closeMuster. Returned both by POST /:id/close and as MusterBoard.summary
 *  once a muster is closed (null before that). */
export interface MusterSummary {
  roster: number;
  safe: number;
  needs_help: number;
  unaccounted: number;
  excluded: number;
  byReason: Record<string, number>;
  durationSeconds: number;
  vehiclesInZone: number | null;
}

// ── POST /preview ────────────────────────────────────────────────────────

/** One resolved roster member as the preview sees them — endpoints filled in
 *  per requested channel, snapshot null when the person has no fix at all.
 *  Roster-first (spec §4.2): nobody is ever excluded from this list, unlike
 *  Broadcast's staleness-filtered audience. */
export interface MusterRosterPerson {
  userId: string;
  name: string;
  endpoints: Partial<Record<BroadcastChannel, string | null>>;
  snapshot: MusterSnapshot | null;
}

/** POST /api/musters/preview response — resolve + estimate + cap-check.
 *  Writes nothing. `capWarning` is advisory only (spec decision 6: a muster
 *  is never blocked by a budget) — never treat it as a reason to refuse. */
export interface MusterPreview {
  people: MusterRosterPerson[];
  estimate: BroadcastEstimate;
  vehiclesInZone: number | null;
  capWarning: BroadcastCapDecision | null;
}

// ── POST / (declare) ────────────────────────────────────────────────────

export interface MusterDeclareResponse {
  id: string;
}

// ── GET / (list) ────────────────────────────────────────────────────────

export interface MusterListItem {
  id: string;
  name: string;
  reason: string | null;
  status: 'open' | 'closed';
  declaredAt: string;
  closedAt: string | null;
  vehiclesInZone: number | null;
  counts: MusterCounts;
}

export interface MusterListResponse {
  items: MusterListItem[];
  nextCursor: string | null;
}

// ── GET /:id (board) ────────────────────────────────────────────────────

/** One roster member on the admin/manager board. Coordinate-bearing by
 *  design (muster.service.ts's own doc comment) — never render this outside
 *  an admin/manager-gated screen. While the muster is OPEN, latitude /
 *  longitude / fixAt / inZone are LIVE (never a stale declare-time value);
 *  while CLOSED they are read from the frozen snapshot_at_close. */
export interface MusterBoardPerson {
  userId: string;
  name: string;
  state: MusterState;
  stateReason: MusterReason | null;
  stateAt: string | null;
  sosAlertId: string | null;
  latitude: number | null;
  longitude: number | null;
  fixAt: string | null;
  inZone: boolean | null;
}

/** GET /api/musters/:id response — the coordinate-bearing admin/manager
 *  board. `summary` is null until the muster is closed. */
export interface MusterBoard {
  id: string;
  name: string;
  reason: string | null;
  status: 'open' | 'closed';
  zone: MusterZoneSpec;
  roster: MusterRosterSpec;
  declaredBy: string | null;
  declaredAt: string;
  closedBy: string | null;
  closedAt: string | null;
  vehiclesInZone: number | null;
  summary: MusterSummary | null;
  people: MusterBoardPerson[];
  counts: MusterCounts;
}

// ── GET /mine ────────────────────────────────────────────────────────────

/** GET /api/musters/mine response item — the member-facing, coordinate-free
 *  read (spec §8.2.2). Returned as a bare array (no pagination wrapper),
 *  only the caller's own row, only in musters that are still open. */
export interface MusterMineView {
  musterId: string;
  name: string;
  reason: string | null;
  declaredAt: string;
  status: 'open' | 'closed';
  state: MusterState;
  stateReason: MusterReason | null;
  stateAt: string | null;
  sosAlertId: string | null;
}

// ── GET /:id/events ──────────────────────────────────────────────────────

/** One row of the append-only muster_events trail. `type` / `source` are
 *  plain strings server-side (muster.service.ts's own MusterEventItem does
 *  not narrow them to an enum — the live vocabulary is
 *  'declared' | 'state_changed' | 'closed' for type and
 *  'self' | 'coordinator' | 'close' for source, but nothing guarantees only
 *  those values ever reach this row), kept that way here rather than
 *  inventing a narrower union the source doesn't declare. `metadata` is
 *  genuinely polymorphic per event type (channels/resolvedCount on
 *  'declared', a summary on 'closed', absent on 'state_changed'). */
export interface MusterEventItem {
  id: string;
  type: string;
  userId: string | null;
  userName: string | null;
  fromState: MusterState | null;
  toState: MusterState | null;
  reason: MusterReason | null;
  source: string;
  actorId: string | null;
  actorName: string | null;
  metadata: unknown;
  createdAt: string;
}

export interface MusterEventsResponse {
  events: MusterEventItem[];
}

// ── GET /:id/notifications ──────────────────────────────────────────────

/** One broadcast_recipients row, read through Muster (spec line 242).
 *  Field-for-field the same table GET /api/broadcasts/:id reads — but note
 *  the server's OWN declared type here (muster.service.ts's
 *  MusterNotificationReceipt) is looser than broadcasts.ts's own
 *  BroadcastReceiptRow for the identical table: user_id/channel/status are
 *  plain strings rather than narrowed unions. Matched to the server's
 *  actual declared type for THIS endpoint, not "corrected" to line up with
 *  the broadcasts.ts shape — the two are independently hand-written
 *  interfaces over the same query, and papering over that mismatch is
 *  exactly the kind of assumption this task exists to avoid.
 *
 *  `segments` was nullable here too until issue #377 narrowed it on both
 *  sides: broadcast_recipients.segments is `INT NOT NULL DEFAULT 1`
 *  (070_broadcast.sql), pinned server-side by an information_schema
 *  assertion so it re-widens loudly if a migration ever drops the NOT NULL.
 *
 *  Both timestamps are ISO-8601 UTC (`"2026-08-04T18:34:35.602Z"`) and safe
 *  to `new Date(...)` on any engine. That is worth stating because it was
 *  NOT true until issue #375: `created_at` was explicitly cast `::text` in
 *  the SQL (`"2026-08-04 18:34:34.552082+00"` — a raw space-separated
 *  Postgres timestamp) while `completed_at` rode through as a driver Date
 *  that Fastify rendered as ISO — two formats behind one declared type, and
 *  the space-separated one is exactly what iOS Hermes/JSC rejects while
 *  Android's V8 tolerates it (the shipped bug on `/api/staff/me/alerts`,
 *  fixed in PR #349). The server now normalises both at the boundary. If
 *  you add a field here, normalise it there too rather than relying on
 *  whatever the pg driver happens to hand back.
 *
 *  `user_id` is NULLABLE (fixed post-review): `broadcast_recipients.user_id`
 *  is `UUID REFERENCES trackizy_users(id) ON DELETE SET NULL` with no
 *  `NOT NULL` (migration 070) — a receipt on a permanent evacuation record
 *  routinely outlives the account it targeted (staff turnover), and the FK
 *  nulls it out rather than cascading the delete. This was originally typed
 *  `string` here (copied verbatim from the server's own, equally wrong,
 *  declared type) and live verification did not catch it because the
 *  fixture's user account still existed in every response observed — wire
 *  verification proves what the server DOES send, not what it CAN'T; for
 *  nullability the schema is the authority, not a sample response. */
export interface MusterNotificationReceipt {
  id: string;
  user_id: string | null;
  channel: string;
  endpoint: string | null;
  status: string;
  reason: string | null;
  provider_ref: string | null;
  segments: number;
  created_at: string;
  completed_at: string | null;
}

/** GET /api/musters/:id/notifications response. `broadcastId` is null only
 *  for a muster that — per the current invariant — never has one;
 *  declareMuster always links one before this function could ever observe
 *  otherwise, but the shape stays honest about the nullable column rather
 *  than assuming that invariant holds forever. */
export interface MusterNotifications {
  broadcastId: string | null;
  receipts: MusterNotificationReceipt[];
  nextCursor: string | null;
}

// ── PATCH /:id/people/:userId (mark) + POST /:id/respond ────────────────

/** Returned by both the coordinator mark and the worker self-report — one
 *  person's current state on one muster. */
export interface MusterPersonState {
  musterId: string;
  userId: string;
  state: MusterState;
  stateReason: MusterReason | null;
  stateSource: MusterSource | null;
  stateBy: string | null;
  stateAt: string | null;
  sosAlertId: string | null;
}

interface CursorParams {
  /** Opaque, server-issued cursor. Both list endpoints paginate on a FIXED
   *  server-side page size (20 for musters, 50 for notifications) — neither
   *  route reads a `limit` param, so one is deliberately not offered here. */
  before?: string;
}

function qsFrom(params?: CursorParams): string {
  if (!params?.before) return '';
  const usp = new URLSearchParams();
  usp.set('before', params.before);
  return `?${usp.toString()}`;
}

export function mustersEndpoints(req: InternalRequest) {
  return {
    /** POST /api/musters/preview — admin/manager only. Resolve + estimate +
     *  cap-check; writes nothing. */
    preview: (data: { roster: MusterRosterSpec; zone: MusterZoneSpec; channels: BroadcastChannel[]; body: string }) =>
      req.request<MusterPreview>('/musters/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** POST /api/musters — admin/manager only. Declares the muster: the
     *  roster + zone SPEC is the only thing sent, never a resolved
     *  recipient list — server-side re-resolution stays the unbypassable
     *  boundary, same as Broadcast's own POST /. Returns 201 with just the
     *  new id, not the full board. */
    declare: (data: {
      name: string;
      reason?: string;
      roster: MusterRosterSpec;
      zone: MusterZoneSpec;
      channels: BroadcastChannel[];
      body: string;
      confirmedCount: number;
    }) =>
      req.request<MusterDeclareResponse>('/musters', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    /** GET /api/musters — admin/manager only. Org-scoped history, open
     *  musters first, cursor-paginated. */
    list: (params?: CursorParams) => req.request<MusterListResponse>(`/musters${qsFrom(params)}`),

    /** GET /api/musters/:id — admin/manager only. The coordinate-bearing
     *  board. */
    get: (id: string) => req.request<MusterBoard>(`/musters/${id}`),

    /** GET /api/musters/mine — any roster member, no admin floor. The
     *  caller's own open musters, coordinate-free by construction. Bare
     *  array, not paginated. */
    mine: () => req.request<MusterMineView[]>('/musters/mine'),

    /** GET /api/musters/:id/events — admin/manager only. The append-only
     *  transition trail, chronological, not paginated. */
    events: (id: string) => req.request<MusterEventsResponse>(`/musters/${id}/events`),

    /** GET /api/musters/:id/notifications — admin/manager only. The
     *  muster's broadcast delivery receipts, cursor-paginated. */
    notifications: (id: string, params?: CursorParams) =>
      req.request<MusterNotifications>(`/musters/${id}/notifications${qsFrom(params)}`),

    /** PATCH /api/musters/:id/people/:userId — admin/manager only, the
     *  coordinator write path. Never legal for `needs_help` — the server
     *  rejects it (that state is backed by a real sos_alerts row only the
     *  subject can raise). */
    mark: (id: string, userId: string, state: MusterState) =>
      req.request<MusterPersonState>(`/musters/${id}/people/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ state }),
      }),

    /** POST /api/musters/:id/respond — any roster member, no admin floor
     *  (spec §5: Muster must degrade to a location-less roll-call without
     *  Workforce). `clientId` MUST be forwarded whenever the caller has
     *  one: it is what makes a retried "I need help" idempotent instead of
     *  firing a second, unlinked, still-live SOS alert — the dedupe index
     *  is partial on `client_id IS NOT NULL`, so a caller that drops this
     *  parameter silently reopens that failure mode. `location` is
     *  forwarded verbatim; the server does not validate or narrow its
     *  shape (same as POST /api/safety/sos). */
    respond: (id: string, state: 'safe' | 'needs_help', location?: unknown, clientId?: string) =>
      req.request<MusterPersonState>(`/musters/${id}/respond`, {
        method: 'POST',
        body: JSON.stringify({ state, location, clientId }),
      }),

    /** POST /api/musters/:id/close — admin/manager only. Freezes every
     *  still-'awaiting' person to 'unaccounted'/'no_response', snapshots
     *  everyone's position, and returns the summary. */
    close: (id: string) => req.request<MusterSummary>(`/musters/${id}/close`, { method: 'POST' }),

    /** GET /api/musters/:id/export — admin/manager only, CSV ONLY. The
     *  spec's `?format=pdf|csv` was superseded by an owner ruling: no
     *  server-side PDF dependency exists in this codebase, and the muster
     *  PDF renders CLIENT-SIDE from the same board data in a later task —
     *  the export route has no `?format=` branch at all. This returns the
     *  URL, not a fetch: the route requires the caller's Bearer token,
     *  which a plain `<a href>`/navigation cannot carry, so triggering the
     *  actual download (fetch + blob, or another way of attaching the
     *  token) is left to the caller. */
    exportUrl: (id: string) => `${req.getBaseUrl()}/musters/${id}/export`,
  };
}
